import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { QueryParamNames } from "../models/enums/query-param-names";
import { NidessoQueryParams } from "../components/router/query-params";
import { UpdateListQueryParamOperator } from "../helpers/update-list-query-param-operators";

// Helper function to dynamically extract query params using the filter type
function extractQueryParams<T extends Object>(
  params: URLSearchParams,
  filterType: { new (): T },
): Partial<T> {
  // Create a temporary instance to get the keys
  const filterInstance = new filterType();
  const queryParamGroup: Partial<T> = {};

  Object.keys(filterInstance).forEach((key) => {
    const paramValue = params.get(
      QueryParamNames[key as keyof typeof QueryParamNames],
    );
    if (paramValue !== null) {
      (queryParamGroup as any)[key] = paramValue;
    }
  });

  return queryParamGroup;
}

export function useQueryParams() {
  const [searchParams, setSearchParams] = useSearchParams();

  const queryParamsDict: { [key: string]: string } = useMemo(() => {
    const entries = Array.from(searchParams.entries());

    const params = entries.reduce((acc, [key, value]) => {
      (acc as any)[key] = value;
      return acc;
    }, {});

    return params;
  }, [searchParams]);

  const getQueryParamGroup = useCallback(
    <T extends Object>(type: new () => T) => {
      return extractQueryParams(searchParams, type);
    },
    [searchParams],
  );

  const updateListQueryParam = (
    paramId: NidessoQueryParams,
    operator: UpdateListQueryParamOperator,
  ) => {
    setSearchParams(operator(paramId, searchParams));
  };

  return {
    setQueryParams: setSearchParams,
    queryParamsDict,
    getQueryParamGroup,
    queryParams: searchParams,
    updateListQueryParam,
  };
}
